/**
 * Générateur — Additionner et soustraire des fractions de dénominateurs
 * quelconques.
 * Attendu ID : 924 | Niveau : 5ème | Domaine : Nombres et calculs / Nombres rationnels
 *
 * Plan de cartes (mise en conformité avec la spécification 924, campagne 5e,
 * 2026-09-21) :
 *
 *   Carte « Réduire au même dénominateur (méthode guidée) » (id guidee)
 *     guidee_texte_trou_saisie : niveau 2 (un dénominateur multiple de l'autre,
 *       une seule fraction à réécrire : facteur, numérateur converti, résultat)
 *       ou niveau 6 (entier ± fraction : l'entier écrit en fraction).
 *     guidee_qcm_unique : niveaux 1 à 4, mode à réponses fermées de la spec :
 *       propositions adossées chacune à une erreur nommée, plus « Aucune de ces
 *       réponses » (juste une fois sur cinq). Quand les dénominateurs sont égaux,
 *       la proposition « oubli de réécriture » est retirée.
 *
 *   Carte « Simplifier d'abord, puis calculer » (id simplifier)
 *     (a·g)/(b·g) ± c/b : la simplification ramène sur le dénominateur du
 *     second terme (niveau 1 après simplification).
 *     simplifier_texte_trou_saisie + simplifier_qcm_unique (résultat irréductible).
 *
 *   Carte « Le dénominateur commun par les multiples » (id multiples)
 *     multiples_texte_trou_saisie : niveau 4 (facteur commun sans être
 *     multiples), listes de multiples affichées, trous = plus petit multiple
 *     commun, numérateurs convertis, résultat.
 *
 *   Carte « Calculs complets, résultat simplifié » (id complets)
 *     complets_texte : deux termes, niveaux 1 à 4, saisie de la fraction
 *       irréductible.
 *     complets_relatifs_texte : niveau 5, numérateurs de signe quelconque,
 *       résultat éventuellement négatif ou nul.
 *     complets_trois_texte : niveau 6, trois termes avec parenthèses ou avec un
 *       entier, deux dénominateurs distincts au plus.
 *     complets_texte_trou_saisie : étapes (numérateurs convertis, résultat sur
 *       le dénominateur commun, forme irréductible si elle diffère).
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\924.json) :
 *   Bornes appliquées : dénominateurs 2..12 ; plus petit dénominateur commun
 *   ≤ 60 (calculé une seule fois, réutilisé partout) ; numérateurs 1..19 en
 *   valeur absolue, négatifs au seul niveau 5 ; deux termes, trois au niveau 6 ;
 *   entier ajouté ou retranché 1..5 ; forme irréductible demandée et annoncée.
 *   Exclusions : addition et soustraction seulement ; aucune fraction de départ
 *   égale à 1 ; aucun numérateur nul, aucun dénominateur 1 parmi les fractions ;
 *   résultat jamais nul hors niveau 5 ; résultat positif hors niveau 5 (les deux
 *   termes d'une soustraction sont échangés AVANT tout calcul) ; deux
 *   dénominateurs distincts au plus, même à trois termes.
 *   Erreurs visées → mots-clés :
 *     numérateurs et dénominateurs additionnés → numerateurs_et_denominateurs_additionnes
 *     dénominateur multiplié, numérateur oublié → numerateur_non_multiplie
 *     une seule des deux fractions réécrite    → une_seule_fraction_reecrite
 *     produit des dénominateurs au lieu du ppcm → produit_des_denominateurs
 *     numérateurs multipliés (confusion + / ×)  → numerateurs_multiplies
 *     signe moins non distribué sur la parenthèse → signe_moins_non_distribue
 *     entier ajouté au seul numérateur         → entier_ajoute_au_numerateur
 *     simplification par des nombres différents → simplification_par_nombres_differents
 *     résultat négatif pris pour une erreur    → resultat_negatif_refuse
 *   Graduation → cartes : niveau 1 → guidee_qcm_unique (cas 0), complets_texte
 *   (cas 0), simplifier_* ; niveau 2 → guidee_texte_trou_saisie, guidee_qcm_unique,
 *   complets_texte, complets_texte_trou_saisie (cas 1) ; niveau 3 → guidee_qcm_unique,
 *   complets_texte, complets_texte_trou_saisie (cas 2) ; niveau 4 →
 *   multiples_texte_trou_saisie, guidee_qcm_unique, complets_texte,
 *   complets_texte_trou_saisie (cas 3) ; niveau 5 → complets_relatifs_texte ;
 *   niveau 6 → complets_trois_texte, guidee_texte_trou_saisie (variante entier).
 *   Non tranché : les réglages « pourcentage de négatifs », « simplification » et
 *   « opérations limitées » n'existent pas dans les gens (pas de réglage
 *   enseignant) : négatifs au niveau 5 seulement, forme irréductible toujours
 *   demandée, opérations mêlées. Fractions de départ irréductibles (spec muette ;
 *   sinon le cas de relation entre dénominateurs serait ambigu). Le résultat nul
 *   n'est réintroduit qu'au niveau 5 (« dernier niveau » lu comme le niveau
 *   des relatifs). Le résultat positif est obtenu par échange des termes avant
 *   calcul (deux termes) et par nouveau tirage (trois termes).
 *
 * Arithmétique entière exacte (produits en croix, zéro flottant).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { gcd } from '../../6eme/fractions/fraction';

const ATTENDU_ID = 924;

const L_GUIDEE = 'Réduire au même dénominateur (méthode guidée)';
const L_SIMPLIFIER = 'Simplifier d\'abord, puis calculer';
const L_MULTIPLES = 'Le dénominateur commun par les multiples';
const L_COMPLETS = 'Calculs complets, résultat simplifié';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Bornes de la spécification ─────────────────────────────────────────
const DEN_MIN = 2;
const DEN_MAX = 12;
const NUM_MAX = 19;
const PPCM_MAX = 60;
const ENTIER_MIN = 1;
const ENTIER_MAX = 5;

const lcm = (a, b) => (a * b) / gcd(a, b);
const AUCUNE = 'Aucune de ces réponses';

/** Relation entre deux dénominateurs : 0 égaux, 1 multiples, 2 premiers entre eux, 3 facteur commun sans être multiples. */
const relation = (b, d) => {
    if (b === d) return 0;
    if (b % d === 0 || d % b === 0) return 1;
    if (gcd(b, d) === 1) return 2;
    return 3;
};

/** KaTeX d'une fraction signée (signe devant la barre), den 1 = entier. */
const katexFrac = (num, den) => {
    if (den === 1) return String(num);
    return `${num < 0 ? '-' : ''}\\dfrac{${Math.abs(num)}}{${den}}`;
};
const katexBrut = (num, den) => `$${katexFrac(num, den)}$`;
const fracTrou = (num, den) => `\\dfrac{{{${num}}}\\rule[-0.55em]{0pt}{0.55em}}{${den}}`;
const T = (s) => `\\text{${s}}`;
// Écriture clavier irréductible ("11/6", "3", "0", "-5/12") — réponses saisie
const fmtReponse = (num, den) => {
    if (num === 0) return '0';
    const g = gcd(Math.abs(num), den) || 1;
    const n = num / g;
    const d = den / g;
    return d === 1 ? String(n) : `${n}/${d}`;
};
// Égalité de valeurs par produits en croix (zéro flottant)
const memeValeur = (n1, d1, n2, d2) => n1 * d2 === n2 * d1;

/** Numérateur 1..19 premier avec den et différent de den (la fraction ne vaut pas 1). */
const tirerNumerateur = (den, max = NUM_MAX) => {
    for (let essai = 0; essai < 60; essai++) {
        const n = randInt(1, max);
        if (n !== den && gcd(n, den) === 1) return n;
    }
    return 1;
};

/**
 * Deux dénominateurs 2..12 dans la relation demandée (0 à 3), ppcm ≤ 60.
 */
const tirerDenominateurs = (cas) => {
    for (let essai = 0; essai < 200; essai++) {
        const b = randInt(DEN_MIN, DEN_MAX);
        const d = cas === 0 ? b : randInt(DEN_MIN, DEN_MAX);
        if (relation(b, d) !== cas || lcm(b, d) > PPCM_MAX) continue;
        return [b, d];
    }
    return [[4, 4], [3, 6], [3, 5], [6, 9]][cas];
};

/**
 * Opération a/b ± c/d dans le cas demandé. Hors relatifs : si la soustraction
 * donnerait un résultat négatif, les deux termes sont ÉCHANGÉS avant tout
 * calcul (spec) ; le résultat n'est jamais nul. Avec relatifs (niveau 5) :
 * signes quelconques, au moins un numérateur négatif, résultat de signe
 * quelconque, nul admis.
 * Retour : { a, b, c, d, cas, op, D, k1, k2, n1, n2, r, reponse }.
 */
const tirerOperation = (cas, { relatifs = false } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const [b, d] = tirerDenominateurs(cas);
        let a = tirerNumerateur(b);
        let c = tirerNumerateur(d);
        const op = randomChoice(['+', '-']);
        if (relatifs) {
            if (randomChoice([true, false])) a = -a;
            if (randomChoice([true, false])) c = -c;
            if (a > 0 && c > 0) continue;
        }
        const termes = [{ n: a, d: b }, { n: c, d: d }];
        if (!relatifs && op === '-' && a * d < c * b) termes.reverse();
        const [t1, t2] = termes;
        const D = lcm(t1.d, t2.d);
        const k1 = D / t1.d;
        const k2 = D / t2.d;
        const n1 = t1.n * k1;
        const n2 = t2.n * k2;
        const r = op === '+' ? n1 + n2 : n1 - n2;
        if (!relatifs && r <= 0) continue;
        return { a: t1.n, b: t1.d, c: t2.n, d: t2.d, cas, op, D, k1, k2, n1, n2, r, reponse: fmtReponse(r, D) };
    }
    const repli = [
        { a: 3, b: 4, c: 1, d: 4 }, { a: 5, b: 6, c: 2, d: 3 }, { a: 2, b: 3, c: 3, d: 5 }, { a: 5, b: 6, c: 7, d: 9 },
    ][cas];
    const D = lcm(repli.b, repli.d);
    const n1 = repli.a * (D / repli.b);
    const n2 = repli.c * (D / repli.d);
    return { ...repli, cas, op: '+', D, k1: D / repli.b, k2: D / repli.d, n1, n2, r: n1 + n2, reponse: fmtReponse(n1 + n2, D) };
};

const latexOperation = (o) => `${katexFrac(o.a, o.b)} ${o.op} ${o.c < 0 ? `\\left(${katexFrac(o.c, o.d)}\\right)` : katexFrac(o.c, o.d)}`;

const CONSIGNE_IRREDUCTIBLE = 'Donne le résultat sous forme de fraction <b>irréductible</b>, tapée avec une barre, par exemple <code>3/4</code>. Si le résultat est un nombre entier, écris-le directement, par exemple <code>2</code>.';

// ═════════ Carte 1 — Méthode guidée ═════════

const genGuideeTrous = () => {
    if (randomChoice([true, false, false])) {
        // Variante ENTIER ± fraction (niveau 6) : l'entier s'écrit en fraction.
        for (let essai = 0; essai < 60; essai++) {
            const d = randInt(DEN_MIN, DEN_MAX);
            const n = randInt(ENTIER_MIN, ENTIER_MAX);
            const c = tirerNumerateur(d);
            const op = randomChoice(['+', '-']);
            const r = op === '+' ? n * d + c : n * d - c;
            if (r <= 0) continue;
            const phrase = `$${n} = \\dfrac{ {{${n * d}}} }{ ${d} } \\qquad ${n} ${op} \\dfrac{${c}}{${d}} = \\dfrac{ {{${r}}} }{ {{${d}}} }$`;
            const payload = makeTexteTrou(phrase,
                '<p><b>Écris l\'entier sous forme de fraction, puis calcule.</b> Complète les numérateurs et le dénominateur.</p>',
                { ...opts(L_GUIDEE), mode: 'saisie' });
            payload._meta.params = {
                niveau: 6, variante: 'entier', n, c, d, op, r, D: d, reponse: fmtReponse(r, d),
                erreurVisee: ['entier_ajoute_au_numerateur'],
            };
            return payload;
        }
    }
    const o = tirerOperation(1);
    // La fraction à réécrire est celle dont le facteur vaut plus de 1.
    const [aFixe, bFixe, cRe, dRe, k] = o.k1 === 1 ? [o.a, o.b, o.c, o.d, o.k2] : [o.c, o.d, o.a, o.b, o.k1];
    const phrase = `$\\dfrac{${cRe}}{${dRe}} = \\dfrac{ ${cRe} \\times {{${k}}} }{ ${dRe} \\times {{${k}}} } = \\dfrac{ {{${cRe * k}}} }{ {{${o.D}}} } \\qquad ${latexOperation(o)} = \\dfrac{ {{${o.r}}} }{ ${o.D} }$`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Réduis les fractions au même dénominateur, puis calcule.</b> Complète chaque étape.</p>',
        { ...opts(L_GUIDEE), mode: 'saisie' });
    payload._meta.params = {
        niveau: 2, variante: 'fractions', ...o, aFixe, bFixe, cRe, dRe, k,
        erreurVisee: ['numerateur_non_multiplie'],
    };
    return payload;
};

/**
 * Familles de distracteurs du mode fermé (spec type_reponse) : chacune est
 * adossée à une erreur nommée. Valeurs en fractions { n, d, code }.
 */
const candidatsDistracteurs = (o) => {
    const signe = o.op === '+' ? 1 : -1;
    const liste = [
        { n: o.a + signe * o.c, d: o.b + o.d, code: 'numerateurs_et_denominateurs_additionnes' },
        { n: o.a * o.c, d: o.b * o.d, code: 'numerateurs_multiplies' },
    ];
    if (o.cas !== 0) liste.push({ n: o.a + signe * o.c, d: o.D, code: 'numerateur_non_multiplie' });
    if (o.cas >= 2) {
        liste.push({ n: o.n1 + signe * o.c, d: o.D, code: 'une_seule_fraction_reecrite' });
        liste.push({ n: o.a + signe * o.n2, d: o.D, code: 'une_seule_fraction_reecrite' });
    }
    // Simplification par des nombres différents : r/g1 sur D/g2, g1 ≠ g2 > 1.
    const divs = (x) => Array.from({ length: x }, (_, i) => i + 1).filter(i => i > 1 && x % i === 0);
    const g1 = divs(Math.abs(o.r));
    const g2 = divs(o.D);
    const pairesG = g1.flatMap(x => g2.filter(y => y !== x).map(y => [x, y]));
    if (pairesG.length) {
        const [x, y] = randomChoice(pairesG);
        liste.push({ n: o.r / x, d: o.D / y, code: 'simplification_par_nombres_differents' });
    }
    return liste;
};

/** Sélectionne `nb` distracteurs valides : positifs, distincts entre eux et de la clé en VALEUR. */
const choisirDistracteurs = (o, nb) => {
    const retenus = [];
    shuffle(candidatsDistracteurs(o)).forEach(c => {
        if (retenus.length >= nb) return;
        if (c.n <= 0 || c.d <= 0) return;
        if (memeValeur(c.n, c.d, o.r, o.D)) return;
        if (retenus.some(x => memeValeur(x.n, x.d, c.n, c.d))) return;
        retenus.push(c);
    });
    return retenus.length >= nb ? retenus : null;
};

const genGuideeQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const cas = randomChoice([0, 1, 2, 3]);
        const o = tirerOperation(cas);
        // Nombre de propositions CONSTANT (spec : quatre propositions plus « aucune de ces
        // réponses » ; une de moins quand les deux dénominateurs sont déjà égaux, la proposition
        // de l'oubli de réécriture serait alors juste). Quand « aucune » est la bonne réponse,
        // elle prend la place de la bonne fraction : il faut donc un distracteur de plus. Un
        // tirage qui ne fournit pas ce compte est REJOUÉ, jamais servi avec moins de propositions.
        const aucuneJuste = randInt(1, 5) === 1;
        const nb = (cas === 0 ? 2 : 3) + (aucuneJuste ? 1 : 0);
        const distracteurs = choisirDistracteurs(o, nb);
        if (!distracteurs) continue;
        const g = gcd(o.r, o.D);
        const correct = katexBrut(o.r / g, o.D / g);
        const choix = [...distracteurs.map(x => katexBrut(x.n, x.d)), AUCUNE];
        if (!aucuneJuste) choix.unshift(correct);
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, aucuneJuste ? AUCUNE : correct,
            '<p><b>Calcule.</b> Donne le résultat sous forme de fraction <b>irréductible</b>.</p>{{element:0}}',
            opts(L_GUIDEE));
        payload.elements_additionnels = {
            version: 1,
            elements: [{ type: 'expression', config: { contenu: `${latexOperation(o)} = ?` } }],
        };
        payload._meta.params = {
            niveau: cas + 1, ...o, aucuneJuste,
            distracteurs: distracteurs.map(x => ({ valeur: katexBrut(x.n, x.d), code: x.code })),
        };
        return payload;
    }
    // Repli déterministe : 5/6 + 2/3 = 3/2 (cas 1)
    const correct = katexBrut(3, 2);
    const payload = makeQcm([correct, katexBrut(7, 9), katexBrut(7, 6), katexBrut(10, 18), AUCUNE], correct,
        '<p><b>Calcule.</b> Donne le résultat sous forme de fraction <b>irréductible</b>.</p>{{element:0}}',
        opts(L_GUIDEE));
    payload.elements_additionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: '\\dfrac{5}{6} + \\dfrac{2}{3} = ?' } }],
    };
    payload._meta.params = {
        niveau: 2, a: 5, b: 6, c: 2, d: 3, cas: 1, op: '+', D: 6, k1: 1, k2: 2, n1: 5, n2: 4, r: 9, reponse: '3/2', aucuneJuste: false,
        distracteurs: [
            { valeur: katexBrut(7, 9), code: 'numerateurs_et_denominateurs_additionnes' },
            { valeur: katexBrut(7, 6), code: 'numerateur_non_multiplie' },
            { valeur: katexBrut(10, 18), code: 'numerateurs_multiplies' },
        ],
    };
    return payload;
};

// ═════════ Carte 2 — Simplifier, puis calculer ═════════

/**
 * (a·g)/(b·g) ± c/b : simplifier la première fraction la ramène EXACTEMENT
 * sur le dénominateur b du second terme. b·g ≤ 12, a·g ≤ 19, résultat
 * (a ± c)/b > 0 (termes échangés avant calcul si besoin), jamais nul.
 */
const tirerSimplifier = () => {
    for (let essai = 0; essai < 100; essai++) {
        const b = randInt(2, 6);
        const g = randInt(2, Math.floor(DEN_MAX / b));
        const c = tirerNumerateur(b);
        let a = tirerNumerateur(b, Math.floor(NUM_MAX / g));
        if (a * g > NUM_MAX) continue;
        let op = randomChoice(['+', '-']);
        // Soustraction : la fraction simplifiable reste en tête ; si le résultat
        // serait négatif, on tire une addition (l'échange casserait la tâche).
        if (op === '-' && a <= c) op = '+';
        const r = op === '+' ? a + c : a - c;
        if (r <= 0) continue;
        return { a, b, g, c, op, r, D: b, reponse: fmtReponse(r, b) };
    }
    return { a: 2, b: 3, g: 4, c: 5, op: '+', r: 7, D: 3, reponse: '7/3' };
};

const genSimplifierTrous = () => {
    const s = tirerSimplifier();
    const { a, b, g, c, op, r } = s;
    const phrase = `$\\dfrac{${a * g}}{${b * g}} = \\dfrac{ {{${a}}} }{ {{${b}}} } \\qquad \\dfrac{${a * g}}{${b * g}} ${op} \\dfrac{${c}}{${b}} = \\dfrac{ {{${r}}} }{ ${b} }$`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Simplifie la première fraction au maximum, puis calcule.</b></p>',
        { ...opts(L_SIMPLIFIER), mode: 'saisie' });
    payload._meta.params = { niveau: 1, ...s, erreurVisee: ['simplification_par_nombres_differents'] };
    return payload;
};

const genSimplifierQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const s = tirerSimplifier();
        const { a, b, g, c, op, r } = s;
        if (gcd(r, b) !== 1) continue; // le résultat (a±c)/b doit être LUI-MÊME irréductible
        const signe = op === '+' ? 1 : -1;
        const candidats = [
            { n: a * g + signe * c, d: b * g + b, code: 'numerateurs_et_denominateurs_additionnes' },
            { n: a * g + signe * c, d: b * g, code: 'numerateur_non_multiplie' },
        ];
        // Simplification fautive : numérateur divisé par g, dénominateur par g2 ≠ g.
        const g2s = Array.from({ length: b * g }, (_, i) => i + 1).filter(i => i > 1 && i !== g && (b * g) % i === 0);
        if (g2s.length) {
            const g2 = randomChoice(g2s);
            const d1 = (b * g) / g2;           // dénominateur obtenu à tort
            const Dm = lcm(d1, b);
            candidats.push({ n: a * (Dm / d1) + signe * c * (Dm / b), d: Dm, code: 'simplification_par_nombres_differents' });
        }
        // Confondre l'addition et la multiplication : les numérateurs multipliés entre eux.
        candidats.push({ n: a * c, d: b, code: 'numerateurs_multiplies' });
        const valides = [];
        candidats.forEach(x => {
            if (x.n <= 0) return;
            if (memeValeur(x.n, x.d, r, b)) return;
            if (valides.some(v => memeValeur(v.n, v.d, x.n, x.d))) return;
            valides.push(x);
        });
        // Même règle que la carte guidée : cinq propositions dans tous les cas, un distracteur de
        // plus quand « aucune » est la bonne réponse, et tirage rejoué si le compte n'y est pas.
        const aucuneJuste = randInt(1, 5) === 1;
        if (valides.length < (aucuneJuste ? 4 : 3)) continue;
        valides.length = aucuneJuste ? 4 : 3;
        const correct = katexBrut(r, b);
        const choix = [...valides.map(x => katexBrut(x.n, x.d)), AUCUNE];
        if (!aucuneJuste) choix.unshift(correct);
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, aucuneJuste ? AUCUNE : correct,
            '<p><b>Simplifie d\'abord la première fraction, puis calcule.</b> Donne le résultat sous forme de fraction <b>irréductible</b>.</p>{{element:0}}',
            opts(L_SIMPLIFIER));
        payload.elements_additionnels = {
            version: 1,
            elements: [{ type: 'expression', config: { contenu: `\\dfrac{${a * g}}{${b * g}} ${op} \\dfrac{${c}}{${b}} = ?` } }],
        };
        payload._meta.params = {
            niveau: 1, ...s, aucuneJuste,
            distracteurs: valides.map(x => ({ valeur: katexBrut(x.n, x.d), code: x.code })),
        };
        return payload;
    }
    // Repli : 8/12 + 5/3 = 7/3
    const correct = katexBrut(7, 3);
    const payload = makeQcm([correct, katexBrut(13, 15), katexBrut(13, 12), katexBrut(10, 3), AUCUNE], correct,
        '<p><b>Simplifie d\'abord la première fraction, puis calcule.</b> Donne le résultat sous forme de fraction <b>irréductible</b>.</p>{{element:0}}',
        opts(L_SIMPLIFIER));
    payload.elements_additionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: '\\dfrac{8}{12} + \\dfrac{5}{3} = ?' } }],
    };
    payload._meta.params = {
        niveau: 1, a: 2, b: 3, g: 4, c: 5, op: '+', r: 7, D: 3, reponse: '7/3', aucuneJuste: false,
        distracteurs: [
            { valeur: katexBrut(13, 15), code: 'numerateurs_et_denominateurs_additionnes' },
            { valeur: katexBrut(13, 12), code: 'numerateur_non_multiplie' },
        ],
    };
    return payload;
};

// ═════════ Carte 3 — Par les multiples (niveau 4) ═════════

const listeMultiples = (b, L) => {
    const n = L / b + 1;
    return Array.from({ length: n }, (_, i) => (i + 1) * b).join(' ; ');
};

const genMultiplesTrous = () => {
    const o = tirerOperation(3);
    const phrase = `Multiples de ${o.b} : ${listeMultiples(o.b, o.D)}…\n`
        + `Multiples de ${o.d} : ${listeMultiples(o.d, o.D)}…\n`
        + `$${T('Plus petit multiple commun non nul : ')} {{${o.D}}}$\n`
        + `$${latexOperation(o)} = ${fracTrou(o.n1, o.D)} ${o.op} ${fracTrou(o.n2, o.D)} = ${fracTrou(o.r, o.D)}$`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Trouve le plus petit dénominateur commun grâce aux listes de multiples, puis calcule.</b></p>',
        { ...opts(L_MULTIPLES), mode: 'saisie' });
    payload._meta.params = {
        niveau: 4, ...o,
        erreurVisee: ['produit_des_denominateurs', 'une_seule_fraction_reecrite', 'numerateur_non_multiplie'],
    };
    return payload;
};

// ═════════ Carte 4 — Calculs complets, résultat simplifié ═════════

const CONSIGNE_COMPLETS = `<p><b>Calcule.</b> ${CONSIGNE_IRREDUCTIBLE}</p>`;
const CONSIGNE_RELATIFS = '<p><b>Calcule.</b> Donne le résultat sous forme de fraction <b>irréductible</b>, tapée avec une barre, par exemple <code>3/4</code> ou <code>-3/4</code>. Si le résultat est un nombre entier, écris-le directement, par exemple <code>2</code>, <code>-1</code> ou <code>0</code>.</p>';
const CONSIGNE_TROIS = '<p><b>Calcule.</b> Simplifie seulement le résultat final. Donne-le sous forme de fraction <b>irréductible</b>, tapée avec une barre, par exemple <code>3/4</code>. Si le résultat est un nombre entier, écris-le directement, par exemple <code>2</code>.</p>';

const payloadSaisie = (latex, reponse, consigne, label) => {
    const payload = makeSaisie(reponse, `${consigne}{{element:0}}`, { ...opts(label), strict: false });
    payload.elements_additionnels = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: `${latex} = ?` } }],
    };
    return payload;
};

const genCompletsSaisie = () => {
    const cas = randomChoice([0, 1, 2, 3]);
    const o = tirerOperation(cas);
    const payload = payloadSaisie(latexOperation(o), o.reponse, CONSIGNE_COMPLETS, L_COMPLETS);
    payload._meta.params = { niveau: cas + 1, ...o, erreurVisee: ['numerateurs_et_denominateurs_additionnes'] };
    return payload;
};

const genCompletsRelatifsSaisie = () => {
    const cas = randomChoice([1, 2, 3]);
    const o = tirerOperation(cas, { relatifs: true });
    const payload = payloadSaisie(latexOperation(o), o.reponse, CONSIGNE_RELATIFS, L_COMPLETS);
    payload._meta.params = { niveau: 5, ...o, erreurVisee: ['resultat_negatif_refuse'] };
    return payload;
};

/**
 * Niveau 6, trois termes, deux dénominateurs distincts au plus :
 *   'parentheses' : a/b − (c/d + e/b) ou a/b + (c/d − e/b) ou a/b − (c/d − e/b)
 *   'entier'      : n ± a/b ± c/d
 * Numérateurs positifs, résultat strictement positif (nouveau tirage sinon).
 */
const tirerTrois = () => {
    for (let essai = 0; essai < 300; essai++) {
        const cas = randomChoice([1, 2, 3]);
        const [b, d] = tirerDenominateurs(cas);
        const D = lcm(b, d);
        const forme = randomChoice(['parentheses', 'entier']);
        if (forme === 'parentheses') {
            const a = tirerNumerateur(b);
            const c = tirerNumerateur(d);
            const e = tirerNumerateur(b);
            const [op1, op2] = randomChoice([['-', '+'], ['+', '-'], ['-', '-']]);
            const interieur = op2 === '+' ? c * (D / d) + e * (D / b) : c * (D / d) - e * (D / b);
            if (interieur === 0) continue;
            const r = op1 === '+' ? a * (D / b) + interieur : a * (D / b) - interieur;
            if (r <= 0) continue;
            return {
                forme, cas, b, d, D, r, reponse: fmtReponse(r, D),
                latex: `\\dfrac{${a}}{${b}} ${op1} \\left( \\dfrac{${c}}{${d}} ${op2} \\dfrac{${e}}{${b}} \\right)`,
                termes: [{ n: a, d: b }, { n: c, d }, { n: e, d: b }],
                erreurVisee: ['signe_moins_non_distribue'],
            };
        }
        const n = randInt(ENTIER_MIN, ENTIER_MAX);
        const a = tirerNumerateur(b);
        const c = tirerNumerateur(d);
        const [op1, op2] = randomChoice([['+', '+'], ['+', '-'], ['-', '+'], ['-', '-']]);
        const r = n * D + (op1 === '+' ? a * (D / b) : -a * (D / b)) + (op2 === '+' ? c * (D / d) : -c * (D / d));
        if (r <= 0) continue;
        return {
            forme, cas, b, d, D, r, n, reponse: fmtReponse(r, D),
            latex: `${n} ${op1} \\dfrac{${a}}{${b}} ${op2} \\dfrac{${c}}{${d}}`,
            termes: [{ n: a, d: b }, { n: c, d }],
            erreurVisee: ['entier_ajoute_au_numerateur'],
        };
    }
    return {
        forme: 'entier', cas: 1, b: 3, d: 6, D: 6, r: 11, n: 2, reponse: '11/6',
        latex: '2 - \\dfrac{1}{3} + \\dfrac{1}{6}',
        termes: [{ n: 1, d: 3 }, { n: 1, d: 6 }],
        erreurVisee: ['entier_ajoute_au_numerateur'],
    };
};

const genCompletsTroisSaisie = () => {
    const t = tirerTrois();
    const payload = payloadSaisie(t.latex, t.reponse, CONSIGNE_TROIS, L_COMPLETS);
    payload._meta.params = { niveau: 6, ...t };
    return payload;
};

const genCompletsTrous = () => {
    const cas = randomChoice([1, 2, 3]);
    const o = tirerOperation(cas);
    const g = gcd(o.r, o.D);
    let phrase = `$${latexOperation(o)} = ${fracTrou(o.n1, o.D)} ${o.op} ${fracTrou(o.n2, o.D)} = ${fracTrou(o.r, o.D)}$`;
    // La forme irréductible n'est demandée que si elle diffère (sinon le trou
    // répéterait la valeur du précédent).
    if (g > 1) phrase += `\n$${T('Sous forme irréductible, le résultat est ')} {{${o.reponse}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Réduis au même dénominateur, puis calcule.</b> Complète les numérateurs${g > 1 ? ', puis donne le résultat sous forme de fraction irréductible, tapée avec une barre, par exemple <code>3/4</code>' : ''}.</p>`,
        { ...opts(L_COMPLETS), mode: 'saisie' });
    payload._meta.params = { niveau: cas + 1, ...o, erreurVisee: ['numerateur_non_multiplie', 'une_seule_fraction_reecrite'] };
    return payload;
};

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'mode fermé de la spec : propositions adossées chacune à une erreur nommée + « Aucune de ces réponses » (juste une fois sur cinq)',
        'chaque distracteur est vérifié différent de la clé et des autres EN VALEUR (produit en croix) ; collision → nouveau tirage complet ; jamais de bouche-trou',
        'dénominateurs égaux : la proposition « oubli de réécriture » est retirée (elle serait juste)',
    ],
    guidee_qcm_unique: {
        numerateurs_et_denominateurs_additionnes: '(a ± c)/(b + d)',
        numerateur_non_multiplie: '(a ± c)/D, D le plus petit dénominateur commun',
        une_seule_fraction_reecrite: 'une fraction convertie, l\'autre non (cas 2 et 3)',
        numerateurs_multiplies: '(a × c)/(b × d), le produit des fractions',
        simplification_par_nombres_differents: 'r/g1 sur D/g2 avec g1 ≠ g2',
    },
    simplifier_qcm_unique: {
        numerateurs_et_denominateurs_additionnes: 'somme brute des numérateurs et des dénominateurs',
        numerateur_non_multiplie: 'numérateurs additionnés sur le plus grand dénominateur',
        simplification_par_nombres_differents: 'première fraction « simplifiée » par g au numérateur et par g2 ≠ g au dénominateur',
    },
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    guidee_texte_trou_saisie: genGuideeTrous,
    guidee_qcm_unique: genGuideeQcm,
    simplifier_texte_trou_saisie: genSimplifierTrous,
    simplifier_qcm_unique: genSimplifierQcm,
    multiples_texte_trou_saisie: genMultiplesTrous,
    complets_texte: genCompletsSaisie,
    complets_relatifs_texte: genCompletsRelatifsSaisie,
    complets_trois_texte: genCompletsTroisSaisie,
    complets_texte_trou_saisie: genCompletsTrous,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', texte: 'Saisie',
    relatifs_texte: 'Saisie (relatifs)', trois_texte: 'Saisie (trois termes)',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'guidee', label: L_GUIDEE, formatsList: buildFormats('guidee') },
    { id: 'simplifier', label: L_SIMPLIFIER, formatsList: buildFormats('simplifier') },
    { id: 'multiples', label: L_MULTIPLES, formatsList: buildFormats('multiples') },
    { id: 'complets', label: L_COMPLETS, formatsList: buildFormats('complets') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
